import { useQuery } from '@tanstack/react-query';
import { UserDTO } from 'probonio-shared-ui/api';
import { apis } from 'probonio-shared-ui/module/api';
import { AdminTenantAccessData, getAdminTenantAccessData } from 'probonio-shared-ui/module/me/permissionSelectors';
import { flattenTenants } from 'probonio-shared-ui/utils/tenant';

export const TENANT_SEARCH_PAGE_SIZE = 10;

export function useAdminTenantAccessData(user: UserDTO | undefined, initialData?: AdminTenantAccessData) {
  return useQuery({
    queryKey: ['adminTenantAccessData', user?.id, user?.roles?.toString(), user?.employments?.toString(), user?.tenantRoles?.toString()],
    queryFn: () => getAdminTenantAccessData(user),
    enabled: !!user,
    initialData,
    staleTime: 5 * 60 * 1000,
  });
}

// The switcher renders search hits directly instead of resolving their hierarchies, so the employee count has
// to come from here - it is an opt-in field on the list endpoint.
const SEARCH_QUERY = { filter: '', includeEmployeeCount: true };

export function useTenantSearch(filter: string, page: number, enabled: boolean) {
  return useQuery({
    queryKey: ['tenants', { ...SEARCH_QUERY, filter, page }, apis.tenants.listTenants.name],
    queryFn: () => apis.tenants.listTenants({ ...SEARCH_QUERY, filter, page, pageSize: TENANT_SEARCH_PAGE_SIZE }).then(resp => resp.data),
    enabled: !!filter && enabled,
    staleTime: Infinity,
  });
}

/**
 * Search for everyone who may not read all tenants: the list endpoint is admin-only, so the hits are matched
 * against the tenants /tenants/hierarchies hands back. The term is part of the query key and nothing is cached
 * across terms, so every search fetches the list again instead of matching against a stale forest.
 */
export function useSwitchableTenantSearch(user: UserDTO | undefined, filter: string, enabled: boolean) {
  return useQuery({
    queryKey: ['switchableTenantSearch', user?.id, filter],
    queryFn: async () => {
      const { switchableTenants } = await getAdminTenantAccessData(user);
      const normalizedFilter = filter.toLowerCase();
      const hits = switchableTenants.flatMap(flattenTenants).filter(node => node.tenant.name.toLowerCase().includes(normalizedFilter));

      // The whole forest goes back to the caller, not just the hits: the tree resolves the ids against it and
      // renders each match with its children.
      return { switchableTenants, tenantIds: hits.map(node => node.tenant.id) };
    },
    enabled: !!filter && enabled,
  });
}
